import { Redirect, Route } from 'react-router-dom'

import { useAuthStore } from '@caradasstore/AuthStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useEntitlementStore } from '@caradasstore/EntitlementStore'
import { useUserStore } from '@caradasstore/UserStore'
import { PageLayout } from '@components/layout'
import { UpgradeRequired } from '@components/billing/UpgradeRequired'
import { FeatureDisabled } from '@components/billing/FeatureDisabled'
import { REDIRECT_URLS } from '../../../constants'
import { moduleAccess, moduleForPath } from '../../../data/entitlements'
import { isClientUser, isTech } from '../../../utils/utils'

export const PrivateRoute = ({
  component: Component,
  usePageLayout = true,
  layoutProps = {},
  componentProps = {},
  // Techs are redirected to the forbidden page instead of rendering the route.
  // The nav entry is hidden for them too, so this only guards direct URL access.
  blockTechAccess = false,
  // System admins (currentUser.is_admin) only — everyone else gets the forbidden
  // page, including center owners/managers. Same deep-link rationale as above.
  adminOnly = false,
  // Read-only (client) users are redirected away. Set it on any route whose whole
  // purpose is to write — a create form, a checklist, a settings screen. Routes that
  // merely contain some write controls stay open and hide those controls instead.
  blockReadOnlyAccess = false,
  ...rest
}) => {
  const authCtx = useAuthStore()
  const isAuthenticated = authCtx.isLoggedIn
  const currentUser = useUserStore((store) => store.currentUser)
  const ccId = useCenterStore((store) => store.ccId)
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const entitlements = useEntitlementStore()
  // currentUser is loaded by PageLayout, so it can be null on a fresh session — only
  // deny once we actually know the role, otherwise a manager/owner deep-link would 403.
  const isTechDenied = blockTechAccess && !!currentUser && isTech(currentUser)
  const isAdminDenied = adminOnly && !!currentUser && !currentUser.is_admin
  const isReadOnlyDenied = blockReadOnlyAccess && isClientUser(currentUser)

  const renderContent = (props) => {
    const content = <Component {...props} {...componentProps} />
    return usePageLayout ? <PageLayout {...layoutProps}>{content}</PageLayout> : content
  }
  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isAuthenticated) {
          return <Redirect to={{ pathname: '/authenticate', state: { from: props.location } }} />
        }
        if (isTechDenied || isAdminDenied || isReadOnlyDenied) {
          return <Redirect to={REDIRECT_URLS.FORBIDDEN_PAGE} />
        }
        // Package gating (CAR-3710): a section the active center's plan does not include
        // renders the upgrade notice in place, inside the layout, so the nav stays usable.
        // Same deep-link rationale as above: nothing is denied until entitlements are known.
        const requiredModule = moduleForPath(props.location?.pathname)
        const access = moduleAccess({ user: currentUser, entitlements, ccId, module: requiredModule, centerDetail })
        // Package first, then role: a section the plan includes but the user's role may not use
        // is "not for you", not "upgrade".
        if (access === 'role') {
          return <Redirect to={REDIRECT_URLS.FORBIDDEN_PAGE} />
        }
        // Switched off by an admin for this center (CAR-4038): explained in place, no upgrade offer.
        if (access === 'disabled') {
          return usePageLayout ? (
            <PageLayout {...layoutProps}>
              <FeatureDisabled />
            </PageLayout>
          ) : (
            <FeatureDisabled />
          )
        }
        if (access === 'plan') {
          const notice = <UpgradeRequired module={requiredModule} />
          return usePageLayout ? <PageLayout {...layoutProps}>{notice}</PageLayout> : notice
        }
        return renderContent(props)
      }}
    />
  )
}
